import React, {useCallback, useEffect, useState} from "react"
import RouterComponent from "./shared/router"
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil"
import {
    globalAccountVerificationProfileState,
    globalBrandConfigState,
    globalBrandModuleSettingState,
    globalBrandSettingState,
    globalColorThemeState,
    globalCurrentPathState,
    globalCurrentPreloadStatusState,
    globalInitLoadingState,
    globalLanguageLoadingState,
    globalMessageState,
    globalNotificationState,
    globalPNServerSettingState,
    globalRunAuthState,
    globalUserDetailState,
    globalUserVerifyState,
} from "./shared/_common/global_state"
import getConfigByName from "./config"
import {ConfigProvider, Image, message, notification, theme} from "antd"
import {useVerification} from "./shared/_common/_authorize"
import usePreload from "./shared/_common/_preload"
import LoadingScreen from "./brand/_auth/loadingScreen/view"
import {Helmet} from "react-helmet"
import {BRAND_NAME, MODE_PRODUCTION} from "./config/_variable"
import {useLocation} from "react-router-dom"
import {useTranslation} from "react-i18next"
import EmptyData from "./brand/component/emptyData/view"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import quarterOfYear from "dayjs/plugin/quarterOfYear"
import {getToken, onMessage} from "firebase/messaging"
import {initializeFirebase, messaging} from "./config/firebaseConfig"
import {updateUserPushNotificationToken} from "./shared/api/graphql/pushNotification"
import {useGetHeaderPushNotificationLogByUserId} from "./shared/pages/announcement/function"
import {checkBrowserFirebaseCompatibility} from "./shared/_common/function"
import {isMobile} from "react-device-detect"
import {apiGetUserVerificationProfile} from "./shared/api/restful/verification";

dayjs.extend(utc)
dayjs.extend(quarterOfYear)

function App() {
    let location = useLocation()
    let {i18n} = useTranslation()

    const [dataLoading, setDataLoading] = useState(true)
    const [brandLoading, setBrandLoading] = useState(true)
    const [layoutLoading, setLayoutLoading] = useState(true)
    const [languageLoading, setLanguageLoading] = useRecoilState(globalLanguageLoadingState)
    const initLoading = useRecoilValue(globalInitLoadingState)

    const [brandConfigState, setBrandConfigState] = useRecoilState(globalBrandConfigState)
    const [preloadStatus, setPreloadStatus] = useRecoilState(globalCurrentPreloadStatusState)
    const brandSettingState = useRecoilValue(globalBrandSettingState)
    const verifyState = useRecoilValue(globalUserVerifyState)
    const runAuthState = useRecoilValue(globalRunAuthState)
    const notificationState = useRecoilValue(globalNotificationState)
    const messageState = useRecoilValue(globalMessageState)
    const setGlobalPathState = useSetRecoilState(globalCurrentPathState)
    const [colorState, setColorState] = useRecoilState(globalColorThemeState)
    const userDetailState = useRecoilValue(globalUserDetailState)
    const brandModuleSetting = useRecoilValue(globalBrandModuleSettingState)
    const PNServerSetting = useRecoilValue(globalPNServerSettingState)
    const [accountVerificationProfile, setAccountVerificationProfile] = useRecoilState(globalAccountVerificationProfileState)
    const urlSearchParams = new URLSearchParams(location.search)
    const token = urlSearchParams.get("token")
    const type = urlSearchParams.get("type")
    const lang = urlSearchParams.get("lang")
    const userIdStorage = localStorage.getItem("userid") || null
    const tokenStorage = localStorage.getItem("a_t") || null
    const affiliate = urlSearchParams.get("affiliate")
    const currentBrand = brandConfigState?.brandName

    // For mobile view redirection
    useEffect(() => {
        if (isMobile && ["blueocean", "antos", "antospnc", "quorvex", "metapromarkets"].includes(BRAND_NAME)) {
            window.open("https://" + window.location.hostname.replace("member", "mobile") + "/" + window.location.hash, "_self")
        }
    }, [isMobile]);

    const {
        getInitialDataWithoutToken,
        // getFirebaseToken,
        // getBrandSetting,
        getDashboardMarketNews,
        // getLanguages,
        // getReferralDataSetting,
        // getDashboardEducation,
        // getDashboardEducationPosting,
        // getDashboardOpportunity,
        // getDashboardNewsletter,
        // getDashboardPromotion,
        // getIconLists,
        // getBrandModuleSetting,
        // getAvailableProduct,
        getUserPreferenceNavigations
    } = usePreload()
    const {
        authorizeUser,
        userGhostLogin,
        trackUserActivity,
        startTokenExpirationCheck,
        handleTokenExpiration,
        verifyCryptoAddressFunction,
        checkTabActivityOnOpening,
        getClientDeviceInfo
    } = useVerification()
    const [_, __, fetchPushNotificationLog, ___] = useGetHeaderPushNotificationLogByUserId()
    const setMessage = useSetRecoilState(globalNotificationState)

    const [api, contextHolder] = notification.useNotification({
        stack: {threshold: 3},
    })
    const [messageApi, messageContextHolder] = message.useMessage()

    const openNotificationWithIcon = (type, msg, title, key, icon, onClose = null) => {
        api[type]({
            icon: icon,
            message: title,
            description: msg,
            key: key,
            onClose: onClose,
        })
    }

    const openMessage = (type, content, duration, key) => {
        messageApi.open({
            type: type,
            content: content,
            duration: duration || 3,
            key: key,
        })
    }

    const brandConfig = async () => {
        const config = await getConfigByName()
        localStorage.setItem("graphqlURL", config.graphqlURL)
        localStorage.setItem("commissionURL", config.commissionURL)
        localStorage.setItem("instrumentsURL", config.instrumentsURL)
        localStorage.setItem("verificationURL", config.verificationURL)
        localStorage.setItem("strategyBuilderURL", config.strategyBuilderURL)
        localStorage.setItem("questionnaireURL", config.questionnaireURL)

        setBrandConfigState(config)
    }

    const layoutCss = async () => {
        await setLayoutLoading(true)
        if (brandConfigState?.defaultLayout) {
            import(`./shared/css/${"layout" + brandConfigState?.defaultLayout}/style.css`)
                .then(() => {
                    console.info("CSS file imported")
                })
                .catch((error) => {
                    console.error("Error importing CSS file:", error)
                })
        }
        setLayoutLoading(false)
    }

    const brandColorCss = async () => {
        const colorStorage = await (localStorage.getItem("color") || brandSettingState?.theme)

        switch (colorStorage) {
            case "dark":
                localStorage.setItem("color", "dark")
                setColorState("dark")
                break
            case "light":
                localStorage.setItem("color", "light")
                setColorState("light")
                break
            // default:
            //     localStorage.setItem("color", "light");
            //     setColorState("light");
        }
    }

    const brandFontCss = () => {
        import(`./brand/font/${currentBrand}.css`)
            .then(() => {
                console.info("CSS file imported")
            })
            .catch((error) => {
                console.error("Error importing CSS file:", error)
            })
    }

    const preloadApp = useCallback(async () => {
        await setPreloadStatus(true)
        await setDataLoading(true)
        const graphql = localStorage.getItem("graphqlURL") || null

        if (graphql) {
            // await getBrandSetting();
            if (languageLoading) {
                // await getLanguages();
                // await getIconLists();
                setLanguageLoading(false)
            }

            await getInitialDataWithoutToken()

            if (token) {
                await userGhostLogin(token)
            } else if (type === "verify-crypto-address") {
                const email = urlSearchParams.get("email")
                const transactionNo = urlSearchParams.get("transaction")
                await verifyCryptoAddressFunction(email, transactionNo)
                await authorizeUser(tokenStorage, userIdStorage)
            } else if (runAuthState === false) {
                await authorizeUser(tokenStorage, userIdStorage)
            }

            if (tokenStorage) {
                // await getLanguages();
                await setDataLoading(false)
                await getDashboardMarketNews()
                // await getReferralDataSetting();
                // await getDashboardEducation();
                // await getDashboardEducationPosting();
                // await getDashboardOpportunity();
                // await getDashboardNewsletter();
                // await getDashboardPromotion();
                // await getAvailableProduct();
            }
        }
    }, [token, brandConfigState, userIdStorage, brandModuleSetting])

    const setupInitialLanguage = async () => {
        let initLang = userDetailState?.language || localStorage.getItem("language") || lang || "en"
        localStorage.setItem("language", initLang)
        i18n.changeLanguage(initLang)
    }

    const preloadBrand = async () => {
        await setupInitialLanguage()
        await setBrandLoading(true)
        await brandConfig()
        // await brandColorCss();
        await brandFontCss()
        setBrandLoading(false)
    }

    const runVerifyingFunction = async () => {
        if (verifyState === true) {
            await checkTabActivityOnOpening()
            await trackUserActivity(10 * 60 * 10000000, handleTokenExpiration)
            // await trackUserActivity(60 * 60 * 1000, handleTokenExpiration); // Debug
            await startTokenExpirationCheck(60 * 10000000)
        }
    }

    const getUpdateFCMToken = async () => {
        try {
            let userId = localStorage.getItem("userid")
            const token = await getToken(messaging, {
                vapidKey: PNServerSetting?.firebase?.vapid,
            })
            localStorage.setItem("fcmToken", token)
            if (userId) {
                await updateUserPushNotificationToken({
                    user: userId,
                    token: token,
                    provider: "firebase",
                    platform: "web",
                })
            }
        } catch (error) {
        }
    }

    const initializeFirebaseApp = async () => {
        try {
            await initializeFirebase(PNServerSetting?.firebase?.config)
            requestNotificationPermission()
        } catch (error) {
        }
    }

    async function requestNotificationPermission() {
        try {
            const {browser} = await getClientDeviceInfo()
            if (browser === "Safari") return

            if (!checkBrowserFirebaseCompatibility() || !messaging) {
                console.error("Your browser does not support Firebase Messaging or Messaging is not initialized.")
                return
            }

            const permission = Notification.permission

            if (permission === "granted") {
                getUpdateFCMToken()
            } else if (permission !== "denied") {
                const result = await Notification.requestPermission()
                if (result === "granted") {
                    getUpdateFCMToken()
                } else {
                    console.warn("User denied notification permission.")
                }
            } else {
                console.warn("User denied notification permission.")
            }
        } catch (error) {
            console.error("An error occurred while requesting notification permission:", error)
        }
    }

    const preloadAccountVerificationProfile = async () => {
        const url = brandConfigState?.verificationURL
        if (url) {
            try {
                const verificationProfile = await apiGetUserVerificationProfile({}, url)

                if (verificationProfile?.status === "Success") {
                    setAccountVerificationProfile(verificationProfile?.data?.[0])
                }
            } catch (e) {
                console.error(e)
            }
        }
    }

    useEffect(() => {
        if (verifyState) {
            preloadAccountVerificationProfile()
        }
    }, [verifyState, brandConfigState?.verificationURL]);

    useEffect(() => {
        if (messaging) {
            onMessage(messaging, (payload) => {
                setMessage({
                    status: "open",
                    title: payload?.notification?.title,
                    msg: (
                        <>
                            {payload?.notification?.body}
                            <Image src={payload?.notification?.image} preview={false}/>
                        </>
                    ),
                    icon: <Image src={payload?.notification?.icon} preview={false}/>,
                })

                fetchPushNotificationLog()
            })
        }
    }, [messaging])

    useEffect(() => {
        if (Object.keys(PNServerSetting)?.length > 0) {
            initializeFirebaseApp()
        }
    }, [PNServerSetting])

    useEffect(() => {
        setupInitialLanguage()
        getUserPreferenceNavigations(userDetailState);
    }, [userDetailState, lang, accountVerificationProfile])

    useEffect(() => {
        preloadBrand()
    }, [])

    useEffect(() => {
        brandColorCss()
    }, [brandSettingState, colorState])

    useEffect(() => {
        layoutCss()
    }, [brandConfigState, token, currentBrand, colorState])

    useEffect(() => {
        if (!preloadStatus && brandConfigState) {
            preloadApp()
        }
    }, [token, brandConfigState, userIdStorage, preloadStatus])

    useEffect(() => {
        runVerifyingFunction()
    }, [verifyState])

    useEffect(() => {
        const {status, msg, title, key, icon, onClose} = notificationState
        if (msg && key !== "systemNotification") {
            openNotificationWithIcon(status, msg, title, key, icon, onClose)
        } else if (MODE_PRODUCTION === false) {
            openNotificationWithIcon(status, msg, title, key, icon, onClose)
        }
    }, [notificationState])

    useEffect(() => {
        const {type, content, duration, key} = messageState
        if (content) {
            openMessage(type, content, duration, key)
        }
    }, [messageState])

    useEffect(() => {
        setPreloadStatus(false)
    })

    useEffect(() => {
        setGlobalPathState(location.pathname)
    }, [location])

    useEffect(() => {
        if (affiliate) localStorage.setItem("affiliate", affiliate)
    }, [affiliate])

    return (
        <>
            <ConfigProvider
                theme={colorState === "dark" ? {algorithm: theme.darkAlgorithm} : {}}
                renderEmpty={() => (
                    <div className={"my-3"}>
                        <EmptyData/>
                    </div>
                )}
            >
                <Helmet>
                    <link rel="stylesheet" href={`./brand/color/${currentBrand}/${colorState}.css`}/>
                    <link rel="icon" href={brandSettingState?.siteFavicon}/>
                    <link rel="apple-touch-icon" href={brandSettingState?.siteFavicon}/>
                    <title>{brandSettingState?.companyName}</title>
                    <meta name="description" content={brandSettingState?.companyName}/>
                </Helmet>
                {contextHolder}
                {messageContextHolder}
                {/*{!dataLoading && !brandLoading && !layoutLoading && !languageLoading && !brandModuleSettingLoading ? (*/}
                {!languageLoading && !brandLoading && !layoutLoading && !initLoading ? <RouterComponent/> :
                    <LoadingScreen/>}
            </ConfigProvider>
        </>
    )
}

export default App
